frappe.provide("erpnext.ui");

// @dokos: max rows rendered per price-list group; keeps the dashboard bounded for
// partners with thousands of Item Prices. See PricingDataService.getPrices/loadData.
const ROW_LIMIT_PER_GROUP = 50;

/**
 * Data Service to handle API interactions
 */
class PricingDataService {
	static async getPriceLists(doctype, doc, start = 0) {
		let filters = { enabled: 1 };
		if (doctype === "Supplier") filters.buying = 1;
		if (doctype === "Customer") filters.selling = 1;
		if (doctype == "Item") {
			if (doc.is_purchase_item && !doc.is_sales_item) {
				filters.buying = 1;
			} else if (!doc.is_purchase_item && doc.is_sales_item) {
				filters.selling = 1;
			}
		}

		const res = await frappe.call({
			method: "frappe.client.get_list",
			args: {
				doctype: "Price List",
				filters: filters,
				fields: ["name", "currency", "buying", "selling"],
				limit_start: start,
				limit_page_length: 20,
			},
		});
		return res.message || [];
	}

	static async getPrices(doctype, docName, itemCode, priceLists = null, rowLimit = null) {
		// @dokos: use the whitelisted, permission-checked endpoint instead of an unrestricted
		// client query. It applies the Item/Supplier/Customer filters server-side and excludes
		// expired/cancelled prices. Scoping to `priceLists` (and capping with `rowLimit`) keeps
		// the dashboard from pulling every price for the whole party in one call.
		const args = { doctype: doctype, docname: docName, item_code: itemCode };
		if (priceLists) args.price_lists = priceLists;
		if (rowLimit) args.price_list_row_limit = rowLimit;

		const response = await frappe.call({
			method: "erpnext.stock.doctype.item.item.get_item_prices",
			args: args,
		});
		return {
			prices: (response.message && response.message.prices) || [],
			hasMoreByPriceList: (response.message && response.message.has_more_by_price_list) || {},
		};
	}
}

erpnext.ui.pricing_dashboard = class ItemPricingDashboard {
	constructor(frm, container) {
		this.frm = frm;
		this.container = container;
		this.doctype = frm.doc.doctype;
		this.price_lists = [];
		this.groupedPrices = {};
		this.hasMoreByPriceList = {};
		this.can_delete = frappe.model.can_delete("Item Price");
		this.start = 0;
		// @dokos: get_item_prices requires read on Item Price; other users would get "Not permitted"
		if (!frappe.model.can_read("Item Price")) return;
		this.init();
	}

	async init() {
		this.price_lists = [];
		this.groupedPrices = {};
		this.hasMoreByPriceList = {};
		this.start = 0;
		await this.loadData();
	}

	async loadData() {
		// Fetch the next 20 Price Lists
		const newPriceLists = await PricingDataService.getPriceLists(this.doctype, this.frm.doc, this.start);

		if (newPriceLists.length > 0) {
			this.price_lists = this.price_lists.concat(newPriceLists);

			// @dokos: only fetch prices for the newly-paged-in price lists, capped per group,
			// instead of re-fetching every price for the whole party on every page/action.
			const { prices, hasMoreByPriceList } = await PricingDataService.getPrices(
				this.doctype,
				this.frm.doc.name,
				this.frm.doc.item_code,
				newPriceLists.map((pl) => pl.name),
				ROW_LIMIT_PER_GROUP
			);
			this.processPrices(prices, hasMoreByPriceList);

			this.start += 20;
			this.render();
		}
	}

	processPrices(prices, hasMoreByPriceList = {}) {
		const today = frappe.datetime.get_today();
		const cutoff = frappe.datetime.add_days(today, -30);

		const filtered = prices.filter((p) => !p.valid_upto || p.valid_upto >= cutoff);

		const newGroups = filtered.reduce((acc, curr) => {
			if (!acc[curr.price_list]) acc[curr.price_list] = [];
			acc[curr.price_list].push(curr);
			return acc;
		}, {});

		// @dokos: merge into the accumulated state so earlier pages aren't dropped.
		Object.assign(this.groupedPrices, newGroups);
		Object.assign(this.hasMoreByPriceList, hasMoreByPriceList);
	}

	// @dokos: Espresso alignment. One shared dk-table for every price list: each list is a <tbody>
	// opened by a tr.dk-grp header row. Layout classes use the `ipd-` prefix (the `dk-` namespace
	// belongs to the shared primitives); buttons and pills come from the erpnext.dokos.ui façade; every class
	// bindEvents() selects on (btn-*) is kept.
	render() {
		const dimensionHeader = this.doctype === "Item" ? __("Partner / Batch") : __("Item");
		const sections = this.price_lists
			.map((pl) => {
				const prices = this.groupedPrices[pl.name] || [];
				return this.getSectionHtml(pl, prices);
			})
			.join("");

		// Show "More" only if the current count is a multiple of 20
		const showMore = this.price_lists.length > 0 && this.price_lists.length % 20 === 0;

		this.container.innerHTML = `
			<div class="ipd-wrapper">
				<div class="dk-table-wrap">
					<table class="dk-table">
						<thead>
							<tr>
								<th class="ipd-col-details"></th>
								<th>${dimensionHeader}</th>
								<th class="dk-r">${__("Rate")}</th>
								<th class="ipd-date">${__("Valid From")}</th>
								<th class="ipd-date">${__("Valid Upto")}</th>
								<th class="dk-act">${__("Actions")}</th>
							</tr>
						</thead>
						${sections}
					</table>
				</div>
				${
					showMore
						? `<div class="ipd-more">${erpnext.dokos.ui.button.html({
								label: `${__("More")}...`,
								css_class: "btn-load-more",
						  })}</div>`
						: ""
				}
			</div>`;

		this.bindEvents();
	}

	getSectionHtml(pl, prices) {
		const hasPrices = prices.length > 0;
		// @dokos: prices are capped per price list (ROW_LIMIT_PER_GROUP); surface a link to the
		// full list instead of silently truncating.
		const hasMore = !!this.hasMoreByPriceList[pl.name];

		return `
			<tbody class="ipd-group">
				${this.getHeaderHtml(pl, hasPrices)}
				${hasPrices ? prices.map((p) => this.getRowHtml(p)).join("") : this.getEmptyRowHtml()}
				${
					hasMore
						? `<tr class="ipd-view-all"><td colspan="6" class="dk-r">
							<a class="btn-view-all-prices" data-pl="${pl.name}">${__("View all prices in this list")}</a>
						</td></tr>`
						: ""
				}
			</tbody>`;
	}

	getHeaderHtml(pl, hasPrices) {
		const plTypeLabel = pl.buying ? __("Buying") : __("Selling");
		return `
			<tr class="dk-grp">
				<td colspan="6">
					<div class="ipd-group-header">
						${erpnext.dokos.ui.pill.html({ label: pl.name, theme: "gray" })}
						<span class="dk-muted">${plTypeLabel}</span>
						<div class="ipd-header-actions">
							${hasPrices ? this.getBulkMenuHtml(pl.name) : ""}
							${erpnext.dokos.ui.button.html({
								label: __("Add Price"),
								icon: "plus",
								variant: "ghost",
								size: "sm",
								css_class: "btn-add-new-price",
								attrs: { "data-pl": JSON.stringify(pl) },
							})}
						</div>
					</div>
				</td>
			</tr>`;
	}

	getBulkMenuHtml(plName) {
		return `
			<div class="dropdown inline-block">
				${erpnext.dokos.ui.button.html({
					label: __("Bulk Actions"),
					icon_right: "chevron-down",
					variant: "ghost",
					size: "sm",
					attrs: { "data-toggle": "dropdown" },
				})}
				<ul class="dropdown-menu dropdown-menu-right">
					<li><a class="dropdown-item btn-bulk-adjust" data-type="increase" data-pl='${plName}'>${__(
			"Increase Prices (%)"
		)}</a></li>
					<li><a class="dropdown-item btn-bulk-adjust" data-type="decrease" data-pl='${plName}'>${__(
			"Decrease Prices (%)"
		)}</a></li>
					<li class="divider"></li>
					<li><a class="dropdown-item btn-bulk-close" data-pl='${plName}'>${__("Bulk Close Prices")}</a></li>
				</ul>
			</div>`;
	}

	getRowHtml(p) {
		const isFuture = p.valid_from > frappe.datetime.get_today();
		const dimensionLabel = this.getDimensionLabel(p);
		const rowData = encodeURIComponent(JSON.stringify(p));

		return `
			<tr class="${isFuture ? "is-future" : ""}" data-name="${p.name}" data-info="${rowData}">
				<td class="ipd-col-details">${erpnext.dokos.ui.button.html({
					icon: "info",
					variant: "ghost",
					size: "sm",
					title: __("Item Price Details"),
					css_class: "btn-view-details",
				})}</td>
				<td>${dimensionLabel}</td>
				<td class="dk-r"><span class="ipd-amount">${format_currency(p.price_list_rate, p.currency)}</span>${
			p.uom ? ` <span class="dk-muted">/ ${frappe.utils.escape_html(p.uom)}</span>` : ""
		}</td>
				<td class="ipd-date">${frappe.datetime.str_to_user(p.valid_from)}</td>
				<td class="ipd-date">${p.valid_upto ? frappe.datetime.str_to_user(p.valid_upto) : "--"}</td>
				<td class="dk-act">${this.getRowActionsHtml()}</td>
			</tr>`;
	}

	getDimensionLabel(p) {
		if (this.doctype !== "Item") {
			return `<strong>${p.item_code}</strong> <br><span class="dk-muted">${p.item_name || ""}</span>`;
		}
		const dims = [];
		if (p.supplier) dims.push(`<strong>${__("Supplier")}:</strong> ${p.supplier}`);
		if (p.customer) dims.push(`<strong>${__("Customer")}:</strong> ${p.customer}`);
		if (p.batch_no) dims.push(`<strong>${__("Batch")}:</strong> ${p.batch_no}`);
		return dims.length > 0 ? dims.join(" | ") : `<em>${__("Standard")}</em>`;
	}

	getRowActionsHtml() {
		return `
			<div class="dropdown">
				${erpnext.dokos.ui.button.html({
					icon: "ellipsis",
					variant: "ghost",
					size: "sm",
					title: __("Actions"),
					attrs: { "data-toggle": "dropdown" },
				})}
				<ul class="dropdown-menu dropdown-menu-right">
					<li><a class="dropdown-item btn-new-revision">${__("New Revision")}</a></li>
					<li><a class="dropdown-item btn-close-price">${__("Close Price")}</a></li>
					${
						this.can_delete
							? `<li class="divider"></li><li><a class="dropdown-item btn-delete-price text-danger">${__(
									"Delete"
							  )}</a></li>`
							: ""
					}
				</ul>
			</div>`;
	}

	getEmptyRowHtml() {
		return `<tr class="dk-empty-row"><td colspan="6">${__(
			"No active price entries for this list."
		)}</td></tr>`;
	}

	bindEvents() {
		const find = (selector) => this.container.querySelectorAll(selector);

		const moreBtn = this.container.querySelector(".btn-load-more");
		if (moreBtn) {
			moreBtn.onclick = async (e) => {
				const $btn = $(e.currentTarget);
				$btn.prop("disabled", true).text(__("Loading..."));
				await this.loadData();
			};
		}

		find(".btn-delete-price").forEach(
			(el) => (el.onclick = (e) => this.handleDelete(this.getRowData(e).name))
		);
		find(".btn-close-price").forEach(
			(el) => (el.onclick = (e) => this.handleClose(this.getRowData(e).name))
		);
		find(".btn-new-revision").forEach(
			(el) => (el.onclick = (e) => this.handleNewRevision(this.getRowData(e)))
		);
		find(".btn-view-details").forEach(
			(el) => (el.onclick = (e) => this.showDetailsDialog(this.getRowData(e)))
		);
		find(".btn-add-new-price").forEach(
			(el) => (el.onclick = (e) => this.handleAddNewPrice(JSON.parse(e.currentTarget.dataset.pl)))
		);
		find(".btn-bulk-adjust").forEach(
			(el) =>
				(el.onclick = (e) =>
					this.handleBulkAdjustment(e.currentTarget.dataset.type, e.currentTarget.dataset.pl))
		);
		find(".btn-bulk-close").forEach(
			(el) => (el.onclick = (e) => this.handleBulkClose(e.currentTarget.dataset.pl))
		);
		find(".btn-view-all-prices").forEach(
			(el) => (el.onclick = (e) => this.viewAllPrices(e.currentTarget.dataset.pl))
		);
	}

	getRowData(e) {
		return JSON.parse(decodeURIComponent(e.target.closest("tr").dataset.info));
	}

	viewAllPrices(plName) {
		// @dokos: capped groups link out to the full, filtered Item Price list view instead of
		// rendering every row inline.
		const filters = { price_list: plName };
		if (this.doctype === "Supplier") filters.supplier = this.frm.doc.name;
		else if (this.doctype === "Customer") filters.customer = this.frm.doc.name;
		else filters.item_code = this.frm.doc.item_code;

		frappe.set_route("List", "Item Price", filters);
	}

	showDetailsDialog(data) {
		const d = new frappe.ui.Dialog({
			title: `${__("Item Price Details")}: ${data.name}`,
			fields: [
				{
					label: __("Price List"),
					fieldname: "price_list",
					fieldtype: "Link",
					options: "Price List",
					read_only: 1,
					default: data.price_list,
				},
				{
					label: __("Supplier"),
					fieldname: "supplier",
					fieldtype: "Link",
					options: "Supplier",
					read_only: 1,
					default: data.supplier,
					depends_on: "eval:doc.supplier",
				},
				{
					label: __("Rate"),
					fieldname: "price_list_rate",
					fieldtype: "Currency",
					options: "currency",
					read_only: 1,
					default: data.price_list_rate,
				},
				{
					label: __("Currency"),
					fieldname: "currency",
					fieldtype: "Link",
					options: "Currency",
					read_only: 1,
					default: data.currency,
				},
				{ fieldtype: "Section Break" },
				{
					label: __("Valid From"),
					fieldname: "valid_from",
					fieldtype: "Date",
					read_only: 1,
					default: data.valid_from,
				},
				{
					label: __("Valid Upto"),
					fieldname: "valid_upto",
					fieldtype: "Date",
					read_only: 1,
					default: data.valid_upto,
				},
				{ fieldtype: "Section Break" },
				{
					label: __("UOM"),
					fieldname: "uom",
					fieldtype: "Link",
					options: "UOM",
					read_only: 1,
					default: data.uom,
				},
				{
					label: __("Packing Unit"),
					fieldname: "packing_unit",
					fieldtype: "Float",
					read_only: 1,
					default: data.packing_unit,
				},
			],
		});

		d.set_secondary_action_label(__("Open Full Form"));
		d.set_secondary_action(() => {
			frappe.set_route("Form", "Item Price", data.name);
		});

		d.show();
	}

	handleDelete(name) {
		frappe.confirm(__("Are you sure you want to delete this price permanently?"), () => {
			frappe.call({
				method: "frappe.client.delete",
				args: { doctype: "Item Price", name: name },
				callback: () => {
					frappe.show_alert({ message: __("Price deleted"), subtitle: "success" });
					this.init();
				},
			});
		});
	}

	handleClose(name) {
		const d = new frappe.ui.Dialog({
			title: __("Close Price"),
			fields: [
				{
					label: __("Valid Upto"),
					fieldname: "valid_upto",
					fieldtype: "Date",
					reqd: 1,
					default: frappe.datetime.get_today(),
				},
			],
			primary_action_label: __("Close Now"),
			primary_action: (values) => {
				frappe.db.set_value("Item Price", name, "valid_upto", values.valid_upto).then(() => {
					d.hide();
					this.init();
				});
			},
		});
		d.show();
	}

	handleNewRevision(oldData) {
		const isSales = this.doctype === "Customer" || (this.doctype === "Item" && !oldData.buying);

		const d = new frappe.ui.Dialog({
			title: __("New Price Revision"),
			fields: [
				{
					label: __("New Rate"),
					fieldname: "price_list_rate",
					fieldtype: "Currency",
					reqd: 1,
					default: oldData.price_list_rate,
				},
				{
					label: __("Starts On"),
					fieldname: "valid_from",
					fieldtype: "Date",
					reqd: 1,
					default: frappe.datetime.add_days(frappe.datetime.get_today(), 1),
				},
				{
					label: __("Close previous price?"),
					fieldname: "auto_close",
					fieldtype: "Check",
					default: 1,
				},
			],
			primary_action: async (values) => {
				if (values.auto_close) {
					await frappe.db.set_value(
						"Item Price",
						oldData.name,
						"valid_upto",
						frappe.datetime.add_days(values.valid_from, -1)
					);
				}

				const newDoc = {
					doctype: "Item Price",
					item_code: oldData.item_code,
					price_list: oldData.price_list,
					price_list_rate: values.price_list_rate,
					valid_from: values.valid_from,
					supplier: oldData.supplier,
					customer: oldData.customer,
					batch_no: oldData.batch_no,
					company: oldData.company,
					currency: oldData.currency,
					buying: isSales ? 0 : 1,
					selling: isSales ? 1 : 0,
				};

				frappe.call({
					method: "frappe.client.insert",
					args: { doc: newDoc },
					callback: () => {
						d.hide();
						this.init();
					},
				});
			},
		});
		d.show();
	}

	handleAddNewPrice(pl) {
		const fields = [
			{ label: __("Rate"), fieldname: "price_list_rate", fieldtype: "Currency", reqd: 1 },
			{
				label: __("Valid From"),
				fieldname: "valid_from",
				fieldtype: "Date",
				reqd: 1,
				default: frappe.datetime.get_today(),
			},
		];

		if (this.doctype !== "Item") {
			fields.unshift({
				label: __("Item"),
				fieldname: "item_code",
				fieldtype: "Link",
				options: "Item",
				reqd: 1,
			});
		} else {
			if (pl.buying)
				fields.unshift({
					label: __("Supplier"),
					fieldname: "supplier",
					fieldtype: "Link",
					options: "Supplier",
				});
			if (pl.selling)
				fields.unshift({
					label: __("Customer"),
					fieldname: "customer",
					fieldtype: "Link",
					options: "Customer",
				});
		}

		const d = new frappe.ui.Dialog({
			title: `${__("Add Price to")} ${pl.name}`,
			fields: fields,
			primary_action: (values) => {
				const doc = {
					doctype: "Item Price",
					price_list: pl.name,
					item_code: values.item_code || this.frm.doc.item_code,
					supplier: values.supplier || (this.doctype === "Supplier" ? this.frm.doc.name : null),
					customer: values.customer || (this.doctype === "Customer" ? this.frm.doc.name : null),
					price_list_rate: values.price_list_rate,
					valid_from: values.valid_from,
					buying: pl.buying,
					selling: pl.selling,
					currency: pl.currency,
				};
				frappe.call({
					method: "frappe.client.insert",
					args: { doc: doc },
					callback: () => {
						d.hide();
						this.init();
					},
				});
			},
		});
		d.show();
	}

	async handleBulkAdjustment(type, plName) {
		// @dokos: fetch this price list's complete, uncapped set on demand -- bulk actions must
		// operate on every price in the list, not just the rows currently rendered (capped).
		frappe.dom.freeze(__("Loading prices..."));
		let pricesToAdjust;
		try {
			({ prices: pricesToAdjust } = await PricingDataService.getPrices(
				this.doctype,
				this.frm.doc.name,
				this.frm.doc.item_code,
				[plName]
			));
		} finally {
			frappe.dom.unfreeze();
		}

		const d = new frappe.ui.Dialog({
			title: type === "increase" ? __("Bulk Increase Prices") : __("Bulk Decrease Prices"),
			fields: [
				{
					label: __("Select Prices"),
					fieldname: "selected_prices",
					fieldtype: "MultiCheck",
					options: pricesToAdjust.map((p) => ({
						label: `${p.item_code} (${format_currency(p.price_list_rate, p.currency)})`,
						value: p.name,
						checked: true,
					})),
					on_change: () => update_preview(),
				},
				{
					label: __("Percentage"),
					fieldname: "percentage",
					fieldtype: "Percent",
					reqd: 1,
					change: () => update_preview(),
				},
				{
					label: __("Preview Calculation"),
					fieldname: "preview_html",
					fieldtype: "HTML",
				},
				{ fieldtype: "Section Break" },
				{
					label: __("Valid From"),
					fieldname: "valid_from",
					fieldtype: "Date",
					reqd: 1,
					default: frappe.datetime.add_days(frappe.datetime.get_today(), 1),
				},
			],
			primary_action_label: __("Update Prices"),
			primary_action: async (values) => {
				const selectedNames = values.selected_prices;
				if (!selectedNames.length) frappe.throw(__("Please select at least one price."));

				const factor =
					type === "increase" ? 1 + values.percentage / 100 : 1 - values.percentage / 100;
				const dayBefore = frappe.datetime.add_days(values.valid_from, -1);

				d.get_primary_btn().prop("disabled", true);

				try {
					for (const name of selectedNames) {
						const old = pricesToAdjust.find((p) => p.name === name);
						await frappe.db.set_value("Item Price", name, "valid_upto", dayBefore);

						let new_doc = { ...old };
						delete new_doc.name;
						new_doc.price_list_rate = old.price_list_rate * factor;
						new_doc.valid_from = values.valid_from;
						new_doc.valid_upto = null;
						new_doc.doctype = "Item Price";

						await frappe.call({ method: "frappe.client.insert", args: { doc: new_doc } });
					}
					d.hide();
					this.init();
					frappe.show_alert({ message: __("Prices updated"), indicator: "green" });
				} finally {
					d.get_primary_btn().prop("disabled", false);
				}
			},
		});

		const update_preview = () => {
			const val = d.get_value("percentage") || 0;
			const selected = d.get_value("selected_prices") || [];
			const sorted_prices = selected.map((id) => pricesToAdjust.find((v) => v.name == id));
			const factor = type === "increase" ? 1 + val / 100 : 1 - val / 100;

			// @dokos: ipd- layout classes, dk-muted instead of Bootstrap text utilities
			let html = `<div class="ipd-preview">
				<label class="control-label">${__("New Rates Preview")}</label>
				<ul class="list-unstyled">`;

			sorted_prices
				.filter((p) => selected.includes(p.name))
				.forEach((p) => {
					const new_rate = p.price_list_rate * factor;
					html += `
					<li class="ipd-preview-item">
						<span>${p.item_code}&nbsp;:&nbsp;</span>
						<span class="dk-muted">${format_currency(p.price_list_rate, p.currency)}</span>
						<span class="ipd-preview-arrow">→</span>
						<strong class="ipd-preview-new">${format_currency(new_rate, p.currency)}</strong>
					</li>`;
				});

			html += `</ul></div>`;
			d.get_field("preview_html").$wrapper.html(html);
		};

		d.show();
		update_preview();
	}

	async handleBulkClose(plName) {
		// @dokos: fetch this price list's complete, uncapped set on demand -- see
		// handleBulkAdjustment for why.
		frappe.dom.freeze(__("Loading prices..."));
		let pricesToClose;
		try {
			({ prices: pricesToClose } = await PricingDataService.getPrices(
				this.doctype,
				this.frm.doc.name,
				this.frm.doc.item_code,
				[plName]
			));
		} finally {
			frappe.dom.unfreeze();
		}

		const d = new frappe.ui.Dialog({
			title: __("Bulk Close Prices"),
			fields: [
				{
					label: __("Select Prices to Close"),
					fieldname: "selected_prices",
					fieldtype: "MultiCheck",
					options: pricesToClose.map((p) => ({
						label: `${p.item_code} (${format_currency(p.price_list_rate, p.currency)})`,
						value: p.name,
						checked: true,
					})),
					columns: 1,
				},
				{
					label: __("Valid Upto"),
					fieldname: "valid_upto",
					fieldtype: "Date",
					reqd: 1,
					default: frappe.datetime.get_today(),
					description: __("The selected prices will no longer be active after this date."),
				},
			],
			primary_action_label: __("Close Prices"),
			primary_action: async (values) => {
				const selectedNames = values.selected_prices;
				if (!selectedNames.length) {
					frappe.throw(__("Please select at least one price to close."));
				}

				d.get_primary_btn().prop("disabled", true);

				try {
					for (const name of selectedNames) {
						await frappe.db.set_value("Item Price", name, "valid_upto", values.valid_upto);
					}

					frappe.show_alert({ message: __("Prices closed successfully"), indicator: "orange" });
					d.hide();
					this.init();
				} catch (e) {
					console.error(e);
					frappe.msgprint(__("An error occurred while closing prices."));
				} finally {
					d.get_primary_btn().prop("disabled", false);
				}
			},
		});

		d.show();
	}
};
